import type { DateRangeValue, PeriodPreset } from "components/PeriodSelector/PeriodSelector";
import type { ReferralData } from "services/referralService/referralService.types";
import type { AggregatedReferralData, ReferralGranularity } from "./ReferralPage.types";

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const numberFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const moneyFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });

const startOfDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addDays = (value: Date, amount: number) => {
  const next = new Date(value);
  next.setDate(next.getDate() + amount);
  return startOfDay(next);
};

const parseApiDate = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

const dayKey = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;

const monthKey = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;

const mondayOf = (value: Date) => {
  const monday = startOfDay(value);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

const dayLabel = (value: Date) =>
  `${String(value.getDate()).padStart(2, "0")} ${MONTHS[value.getMonth()]}`;

const monthLabel = (value: Date) =>
  `${MONTHS[value.getMonth()]} ${String(value.getFullYear()).slice(2)}`;

const bucketKey = (value: Date, granularity: ReferralGranularity) => {
  if (granularity === "month") return monthKey(value);
  if (granularity === "week") return dayKey(mondayOf(value));
  return dayKey(value);
};

const bucketLabel = (value: Date, granularity: ReferralGranularity) => {
  if (granularity === "month") return monthLabel(value);
  if (granularity === "week") return dayLabel(mondayOf(value));
  return dayLabel(value);
};

const getGranularity = (range: DateRangeValue): ReferralGranularity => {
  const span = Math.max(0, Math.round((startOfDay(range.to).getTime() - startOfDay(range.from).getTime()) / DAY_MS));
  if (span <= 45) return "day";
  if (span <= 200) return "week";
  return "month";
};

export const getReferralEarliestDate = (data: ReferralData[]) => {
  if (!data.length) return null;
  return data.reduce<Date | null>((earliest, item) => {
    const date = parseApiDate(item.date);
    return !earliest || date < earliest ? date : earliest;
  }, null);
};

export const getReferralPresetRange = (
  preset: Exclude<PeriodPreset, "custom">,
  earliestDate: Date | null,
  maxDate: Date,
): DateRangeValue => {
  const to = startOfDay(maxDate);
  if (preset === "all") {
    return { from: earliestDate ? startOfDay(earliestDate) : to, to };
  }

  const days = Number(preset);
  return { from: addDays(to, -(days - 1)), to };
};

export const aggregateReferralData = (
  data: ReferralData[],
  range: DateRangeValue,
): AggregatedReferralData => {
  const from = startOfDay(range.from);
  const to = startOfDay(range.to);
  const granularity = getGranularity({ from, to });
  const buckets = new Map<string, { label: string; joined: number }>();

  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) {
    const key = bucketKey(cursor, granularity);
    if (!buckets.has(key)) {
      buckets.set(key, { label: bucketLabel(cursor, granularity), joined: 0 });
    }
  }

  let running = 0;
  data.forEach((item) => {
    const date = parseApiDate(item.date);
    if (date < from) {
      running += item.referral_count;
      return;
    }
    if (date > to) return;
    const bucket = buckets.get(bucketKey(date, granularity));
    if (bucket) bucket.joined += item.referral_count;
  });

  let referralsInPeriod = 0;
  const points = [...buckets.values()].map((bucket) => {
    referralsInPeriod += bucket.joined;
    running += bucket.joined;
    return {
      label: bucket.label,
      joined: bucket.joined,
      total: running,
    };
  });

  return { granularity, points, referralsInPeriod };
};

export const formatReferralPeriod = (range: DateRangeValue) =>
  `${range.from.toLocaleDateString("ru-RU")} — ${range.to.toLocaleDateString("ru-RU")}`;

export const getReferralChartTitle = (granularity: ReferralGranularity) => {
  const suffix = granularity === "day" ? "по дням" : granularity === "week" ? "по неделям" : "по месяцам";
  return `Приглашённые ученики ${suffix}`;
};

export const getReferralBarLabel = (granularity: ReferralGranularity) =>
  granularity === "day"
    ? "новые за день"
    : granularity === "week"
      ? "новые за неделю"
      : "новые за месяц";

export const getReferralBarMaxSize = (granularity: ReferralGranularity) =>
  granularity === "day" ? 14 : granularity === "week" ? 22 : 30;

export const formatReferralNumber = (value: number) => numberFormatter.format(Math.round(value));

export const formatReferralMoney = (value: number) => `${moneyFormatter.format(value)} руб.`;

export const formatReferralConversion = (referrals: number, clicks: number) => {
  const value = clicks > 0 ? (referrals / clicks) * 100 : 0;
  return {
    value,
    label: `${value.toFixed(1).replace(".", ",")}%`,
  };
};
